import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Code, Smartphone, ShoppingCart, Globe, FileText, Palette, Check, Star, Menu, X, Loader2, CheckCircle2, Mail, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { services, portfolioProjects, testimonials, stats } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { captureEvent } from '../lib/posthog';
import { toast } from '../components/ui/sonner';
import { format } from 'date-fns';

// Quiz questions configuration
const quizQuestions = [
  {
    id: 'projectType',
    question: "What do you need?",
    type: 'multiple-choice',
    required: true,
    options: ['Website Redesign', 'New Website', 'E-commerce Site', 'Mobile App', 'Other'],
    field: 'projectType'
  },
  {
    id: 'budget',
    question: "What's your budget?",
    type: 'multiple-choice',
    required: true,
    options: ['Under $1,000', '$1,000 - $5,000', '$5,000 - $10,000', '$10,000+', 'Not sure yet'],
    field: 'budget'
  },
  {
    id: 'message',
    question: "Tell us about your project",
    type: 'textarea',
    required: false,
    placeholder: 'Any additional details...',
    skipText: 'Skip',
    field: 'message'
  }
];

// Contact details fields (shown together on one page)
const contactFields = [
  {
    id: 'name',
    question: "What's your name?",
    type: 'text',
    required: true,
    placeholder: 'John Doe',
    field: 'name'
  },
  {
    id: 'company',
    question: "What's your company name?",
    type: 'text',
    required: false,
    placeholder: 'Your Company',
    field: 'company'
  },
  {
    id: 'email',
    question: "What's your email?",
    type: 'email',
    required: true,
    placeholder: 'john@company.com',
    field: 'email'
  }
];

const Home = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: '',
    budget: '',
    consultationDate: null,
    consultationTime: ''
  });
  
  // Track logged visits to prevent duplicates
  const loggedVisitsRef = useRef(new Set());

  // Handle hash navigation and campaign tracking
  useEffect(() => {
    // Get hash from location or window
    const hash = location.hash || window.location.hash;
    
    // Check for campaign ID and sentEmailId in query parameters (both in search and hash)
    const searchParams = new URLSearchParams(window.location.search);
    let campaignId = searchParams.get('c');
    let sentEmailId = searchParams.get('e');
    
    // If not in search, check hash for query parameters (e.g., #contact?c=...&e=...)
    if (hash && hash.includes('?')) {
      const hashParts = hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        if (!campaignId) {
          campaignId = hashParams.get('c');
        }
        if (!sentEmailId) {
          sentEmailId = hashParams.get('e');
        }
      }
    }
    
    if (campaignId) {
      // Create a unique key for this visit (campaignId + sentEmailId + current URL)
      const visitKey = `${campaignId}-${sentEmailId || 'none'}-${window.location.href}`;
      
      // Only log if we haven't logged this exact visit before
      if (!loggedVisitsRef.current.has(visitKey)) {
        // Mark this visit as logged
        loggedVisitsRef.current.add(visitKey);
        
        // Store campaign ID for tracking
        localStorage.setItem('campaignId', campaignId);
        if (sentEmailId) {
          localStorage.setItem('sentEmailId', sentEmailId);
        }
        
        // Log lead visit to backend
        const logLeadVisit = async () => {
          try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            await fetch(`${apiUrl}/api/leads/visit`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ 
                campaignId,
                sentEmailId: sentEmailId || null,
              }),
            });
          } catch (error) {
            console.error('Error logging lead visit:', error);
            // Fail silently - don't interrupt user experience
          }
        };
        
        logLeadVisit();
        
        // Track campaign visit in PostHog
        captureEvent('campaign_visit', {
          campaign_id: campaignId,
          sent_email_id: sentEmailId || null,
        });
      }
    }

    // Handle hash navigation - check if hash starts with #contact
    if (hash && hash.startsWith('#contact')) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Calculate total steps: quiz questions + 1 contact details page + 1 consultation scheduling page
  const totalSteps = quizQuestions.length + 2;
  const isContactDetailsPage = currentStep === quizQuestions.length;
  const isConsultationPage = currentStep === quizQuestions.length + 1;
  
  // Get current question (or null if on contact details or consultation page)
  const currentQuestion = (isContactDetailsPage || isConsultationPage) ? null : quizQuestions[currentStep];

  // Handle input change (no auto-advance)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle multiple choice selection (no auto-advance)
  const handleOptionSelect = (value) => {
    setFormData({ ...formData, [currentQuestion.field]: value });
  };

  // Check if current question/page is valid
  const isCurrentQuestionValid = () => {
    if (isContactDetailsPage) {
      // Validate contact details page: name and email are required
      const nameValid = formData.name && formData.name.trim();
      const emailValid = formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      return nameValid && emailValid;
    } else if (isConsultationPage) {
      // Consultation page is optional - always valid
      return true;
    } else {
      // Validate regular question
      const value = formData[currentQuestion.field];
      if (currentQuestion.required) {
        if (!value || !value.trim()) return false;
        if (currentQuestion.type === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(value);
        }
        return true;
      }
      return true; // Optional questions are always valid
    }
  };

  // Handle next question
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Show review screen
      setShowReview(true);
    }
  };

  // Time slots for consultation
  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM'
  ];

  // Handle previous question
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setShowReview(false);
    }
  };

  // Handle skip (for optional questions)
  const handleSkip = () => {
    handleNext();
  };

  // Handle edit from review
  const handleEdit = (stepIndex) => {
    setCurrentStep(stepIndex);
    setShowReview(false);
  };

  // Helper function to extract query parameters from current URL
  const getQueryParams = () => {
    let campaignId = null;
    let sentEmailId = null;
    
    // Check search params first (e.g., ?c=xxx&e=yyy)
    const searchParams = new URLSearchParams(window.location.search);
    campaignId = searchParams.get('c') || null;
    sentEmailId = searchParams.get('e') || null;
    
    // Check hash params if not found in search (e.g., #contact?c=xxx&e=yyy)
    const hash = window.location.hash || '';
    if ((!campaignId || !sentEmailId) && hash.includes('?')) {
      const hashParts = hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        if (!campaignId) {
          campaignId = hashParams.get('c') || null;
        }
        if (!sentEmailId) {
          sentEmailId = hashParams.get('e') || null;
        }
      }
    }
    
    // Fallback to localStorage if URL doesn't have params
    if (!campaignId) {
      campaignId = localStorage.getItem('campaignId');
    }
    if (!sentEmailId) {
      sentEmailId = localStorage.getItem('sentEmailId');
    }
    
    return { campaignId, sentEmailId };
  };

  // Handle final submission
  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name || !formData.email || !formData.projectType || !formData.budget) {
      toast.error('Please complete all required fields', {
        description: 'Name, email, project type, and budget are required.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get campaign ID and sentEmailId from URL (primary) or localStorage (fallback)
      const { campaignId, sentEmailId } = getQueryParams();
      
      // Track form submission in PostHog
      captureEvent('contact_form_submitted', {
        project_type: formData.projectType,
        budget: formData.budget,
        company: formData.company ? 'yes' : 'no',
        message_length: formData.message ? formData.message.length : 0,
        campaign_id: campaignId || null,
        sent_email_id: sentEmailId || null,
        has_consultation: !!(formData.consultationDate && formData.consultationTime),
      });

      // Prepare submission data
      const submissionData = {
        ...formData,
        consultationDate: formData.consultationDate ? formData.consultationDate.toISOString() : null,
        campaignId: campaignId || null,
        sentEmailId: sentEmailId || null,
      };

      // Call backend API
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      // Success - show success state
      setIsSuccess(true);
      setShowReview(false);
      
      // Reset form data
      setFormData({ name: '', email: '', company: '', projectType: '', message: '', budget: '', consultationDate: null, consultationTime: '' });
      setCurrentStep(0);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message', {
        description: error.message || 'Please try again later or contact us directly.',
      });
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setIsSuccess(false);
    setIsSubmitting(false);
    setShowReview(false);
    setCurrentStep(0);
    setFormData({ name: '', email: '', company: '', projectType: '', message: '', budget: '', consultationDate: null, consultationTime: '' });
    setQuizOpen(false);
  };

  const handleOpenQuiz = () => {
    setQuizOpen(true);
    setCurrentStep(0);
    setShowReview(false);
    setIsSuccess(false);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-container">
          <div className="nav-logo">Axios Development</div>
          
          {/* Desktop Navigation */}
          <div className="nav-links-desktop">
            <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
            <button onClick={() => scrollToSection('portfolio')} className="nav-link">Portfolio</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link">Testimonials</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
          </div>

          {/* Mobile Menu Button */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="nav-links-mobile">
            <button onClick={() => scrollToSection('services')} className="nav-link-mobile">Services</button>
            <button onClick={() => scrollToSection('portfolio')} className="nav-link-mobile">Portfolio</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link-mobile">Testimonials</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link-mobile">Contact</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">WE BUILD DIGITAL EXPERIENCES THAT MATTER</h1>
          <p className="hero-subtitle">Custom websites and apps for ambitious businesses. E-commerce, ordering systems, content platforms, and more.</p>
          <div className="hero-cta-group">
            <Button className="btn-primary" onClick={() => scrollToSection('contact')}>
              Get Started <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button className="btn-secondary" onClick={() => scrollToSection('portfolio')}>
              View Our Work
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">SERVICES</h2>
            <p className="section-subtitle">Comprehensive digital solutions tailored to your business needs</p>
          </div>
          
          <div className="services-grid">
            {services.map((service) => (
              <Card key={service.id} className="service-card">
                <CardContent className="service-card-content">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="service-feature-item">
                        <Check size={16} className="feature-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">FEATURED WORK</h2>
            <p className="section-subtitle">Recent projects that showcase our expertise and creativity</p>
          </div>
          
          <div className="portfolio-grid">
            {portfolioProjects.map((project) => (
              <Card key={project.id} className="portfolio-card">
                <CardContent className="portfolio-card-content">
                  <div className="portfolio-category">{project.category}</div>
                  <h3 className="portfolio-title">{project.title}</h3>
                  <p className="portfolio-description">{project.description}</p>
                  <div className="portfolio-tags">
                    {project.tags.map((tag, idx) => (
                      <span key={idx} className="portfolio-tag">{tag}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">CLIENT TESTIMONIALS</h2>
            <p className="section-subtitle">What our clients say about working with us</p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card">
                <CardContent className="testimonial-card-content">
                  <div className="testimonial-stars">
                    {[...Array(testimonial.rating)].map((_, idx) => (
                      <Star key={idx} size={20} fill="#d9fb06" color="#d9fb06" />
                    ))}
                  </div>
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2 className="contact-title">LET'S BUILD SOMETHING AMAZING</h2>
              <p className="contact-subtitle">Ready to start your project? Get in touch and let's discuss how we can help bring your vision to life.</p>
              
              <div className="contact-features">
                <div className="contact-feature">
                  <Check size={20} className="contact-feature-icon" />
                  <span>Free initial consultation</span>
                </div>
                <div className="contact-feature">
                  <Check size={20} className="contact-feature-icon" />
                  <span>Custom proposal within 48 hours</span>
                </div>
                <div className="contact-feature">
                  <Check size={20} className="contact-feature-icon" />
                  <span>Transparent pricing and timeline</span>
                </div>
                <div className="contact-feature">
                  <Check size={20} className="contact-feature-icon" />
                  <span>Dedicated project manager</span>
                </div>
              </div>
            </div>

            <Card className="contact-form-card">
              <CardContent className="contact-form-content" style={{ padding: '60px 40px', textAlign: 'center' }}>
                <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                  <h3 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px', color: '#ffffff' }}>
                    Get Started Today
                  </h3>
                  <p style={{ fontSize: '16px', color: '#dfddd6', marginBottom: '32px', lineHeight: '1.6' }}>
                    Answer a few quick questions and we'll get back to you with a custom proposal.
                  </p>
                  <Button 
                    onClick={handleOpenQuiz}
                    className="btn-primary btn-full-width"
                    style={{ fontSize: '18px', padding: '18px 32px' }}
                  >
                    Start Your Project <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 className="footer-logo">Axios Development</h3>
              <p className="footer-tagline">Building digital excellence since 2014</p>
            </div>

            <div className="footer-section">
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>Web Development</li>
                <li>Mobile Apps</li>
                <li>E-Commerce</li>
                <li>UI/UX Design</li>
              </ul>
            </div>

            <div className="footer-section">
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>About Us</li>
                <li>Portfolio</li>
                <li>Careers</li>
                <li>Contact</li>
              </ul>
            </div>

            <div className="footer-section">
              <h4 className="footer-heading">Connect</h4>
              <ul className="footer-links">
                <li>LinkedIn</li>
                <li>Twitter</li>
                <li>Instagram</li>
                <li>GitHub</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 Axios Development. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Quiz Modal */}
      <Dialog open={quizOpen} onOpenChange={setQuizOpen}>
        <DialogContent 
          className="quiz-modal-content"
          style={{
            maxWidth: '900px',
            width: '100vw',
            maxHeight: 'calc(100vh - 80px)',
            height: 'calc(100vh - 80px)',
            top: '80px',
            left: '0',
            right: '0',
            bottom: '0',
            transform: 'none',
            overflowY: 'auto',
            padding: '0',
            margin: '0',
            background: '#ffffff',
            borderRadius: '0',
            border: 'none',
            boxShadow: 'none',
            color: '#1a1c1b',
            zIndex: 1001
          }}
        >
          <div className="quiz-modal-inner" style={{ padding: '24px 20px', minHeight: '100%', color: '#1a1c1b', position: 'relative' }}>
            {isSuccess ? (
              <div style={{ textAlign: 'center', animation: 'fadeIn 0.3s ease-in' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  background: '#d9fb06', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: 'scaleIn 0.5s ease-out'
                }}>
                  <CheckCircle2 size={48} style={{ color: '#1a1c1b' }} />
                </div>
                <h3 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1c1b' }}>
                  Message Sent Successfully!
                </h3>
                <p style={{ fontSize: '16px', color: '#666', marginBottom: '32px', lineHeight: '1.6' }}>
                  Thank you for reaching out. We've received your message and will get back to you within 48 hours.
                </p>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '12px', 
                  marginBottom: '32px',
                  background: '#f9f9f9',
                  padding: '20px',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#333' }}>
                    <Mail size={20} style={{ color: '#d9fb06' }} />
                    <span>Check your email for a confirmation</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#333' }}>
                    <Check size={20} style={{ color: '#d9fb06' }} />
                    <span>We'll respond within 48 hours</span>
                  </div>
                </div>
                <Button 
                  onClick={handleResetForm}
                  className="btn-primary btn-full-width"
                >
                  Close
                </Button>
              </div>
            ) : showReview ? (
              <div style={{ animation: 'fadeIn 0.3s ease-in' }}>
                <DialogHeader style={{ marginBottom: '32px' }}>
                  <DialogTitle style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a1c1b', marginBottom: '8px' }}>
                    Review Your Answers
                  </DialogTitle>
                  <DialogDescription style={{ fontSize: '16px', color: '#666', marginTop: '8px' }}>
                    Please review your information before submitting
                  </DialogDescription>
                </DialogHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '32px', maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
                  {/* Quiz Questions */}
                  {quizQuestions.map((q, index) => {
                    const value = formData[q.field];
                    if (!value && !q.required) return null;
                    return (
                      <div 
                        key={q.id} 
                        style={{ 
                          padding: '20px', 
                          background: '#f9f9f9', 
                          borderRadius: '12px', 
                          border: '1px solid #e5e5e5',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f5f5f5';
                          e.currentTarget.style.borderColor = '#d9fb06';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f9f9f9';
                          e.currentTarget.style.borderColor = '#e5e5e5';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '12px', color: '#888680', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                              {q.question}
                            </p>
                            <p style={{ fontSize: '18px', fontWeight: '600', color: '#1a1c1b' }}>
                              {value || 'Not provided'}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(index)}
                            className="btn-secondary"
                            style={{ marginLeft: '16px', minWidth: '80px' }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {/* Consultation Scheduling */}
                  {formData.consultationDate && formData.consultationTime && (
                    <div 
                      style={{ 
                        padding: '20px', 
                        background: '#f9f9f9', 
                        borderRadius: '12px', 
                        border: '1px solid #e5e5e5',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f5f5f5';
                        e.currentTarget.style.borderColor = '#d9fb06';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f9f9f9';
                        e.currentTarget.style.borderColor = '#e5e5e5';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: '12px', color: '#888680', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                            Consultation Scheduled
                          </p>
                          <p style={{ fontSize: '18px', fontWeight: '600', color: '#1a1c1b' }}>
                            {format(formData.consultationDate, 'EEEE, MMMM d, yyyy')} at {formData.consultationTime}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(quizQuestions.length + 1)}
                          className="btn-secondary"
                          style={{ marginLeft: '16px', minWidth: '80px', color: '#1a1c1b' }}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Contact Details */}
                  {contactFields.map((field, index) => {
                    const value = formData[field.field];
                    if (!value && !field.required) return null;
                    const editIndex = quizQuestions.length; // Contact details page index
                    return (
                      <div 
                        key={field.id} 
                        style={{ 
                          padding: '20px', 
                          background: '#f9f9f9', 
                          borderRadius: '12px', 
                          border: '1px solid #e5e5e5',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f5f5f5';
                          e.currentTarget.style.borderColor = '#d9fb06';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f9f9f9';
                          e.currentTarget.style.borderColor = '#e5e5e5';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '12px', color: '#888680', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                              {field.question}
                            </p>
                            <p style={{ fontSize: '18px', fontWeight: '600', color: '#1a1c1b' }}>
                              {value || 'Not provided'}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(editIndex)}
                            className="btn-secondary"
                            style={{ marginLeft: '16px', minWidth: '80px' }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="quiz-nav-buttons" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setShowReview(false)}
                    style={{ 
                      flex: 1, 
                      borderRadius: '8px',
                      color: '#1a1c1b',
                      border: '1px solid #1a1c1b',
                      backgroundColor: 'transparent',
                      padding: '14px 28px',
                      fontSize: '16px',
                      fontWeight: '600',
                      minHeight: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.color = '#1a1c1b';
                      e.currentTarget.style.borderColor = '#1a1c1b';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#1a1c1b';
                      e.currentTarget.style.borderColor = '#1a1c1b';
                    }}
                  >
                    <ChevronLeft size={18} style={{ color: '#1a1c1b' }} />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{ 
                      flex: 1,
                      minWidth: '140px',
                      borderRadius: '8px',
                      color: isSubmitting ? '#1a1c1b' : '#1a1c1b',
                      border: `1px solid ${isSubmitting ? '#e5e5e5' : '#1a1c1b'}`,
                      backgroundColor: isSubmitting ? '#f5f5f5' : 'transparent',
                      opacity: isSubmitting ? 0.6 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      padding: '14px 28px',
                      fontSize: '16px',
                      fontWeight: '600',
                      minHeight: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#1a1c1b' }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message <ArrowRight size={18} style={{ color: '#1a1c1b' }} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ animation: 'fadeIn 0.3s ease-in' }}>
                {/* Progress Indicator */}
                <div style={{ marginBottom: '40px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <span style={{ fontSize: '14px', color: '#888680', fontWeight: '600', letterSpacing: '0.5px' }}>
                      QUESTION {currentStep + 1} OF {totalSteps}
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '8px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div 
                      style={{ 
                        width: `${((currentStep + 1) / totalSteps) * 100}%`, 
                        height: '100%', 
                        background: 'linear-gradient(90deg, #d9fb06 0%, #b8d905 100%)', 
                        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(217, 251, 6, 0.4)'
                      }} 
                    />
                  </div>
                </div>

                {/* Question Display */}
                <div style={{ marginBottom: '48px', minHeight: '280px' }}>
                  {isConsultationPage ? (
                    /* Consultation Scheduling Page */
                    <div>
                      <h2 style={{ 
                        fontSize: '36px', 
                        fontWeight: 'bold', 
                        marginBottom: '16px', 
                        color: '#1a1c1b', 
                        lineHeight: '1.2',
                        letterSpacing: '-0.5px'
                      }}>
                        Schedule a Consultation
                      </h2>
                      <p style={{ 
                        fontSize: '16px', 
                        color: '#666', 
                        marginBottom: '32px',
                        lineHeight: '1.6'
                      }}>
                        Choose a date and time that works for you (optional)
                      </p>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                        {/* Calendar */}
                        <div>
                          <Label style={{ 
                            display: 'block', 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            marginBottom: '12px', 
                            color: '#1a1c1b' 
                          }}>
                            <CalendarIcon className="inline mr-2" size={18} />
                            Select Date
                          </Label>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            padding: '20px',
                            background: '#f9f9f9',
                            borderRadius: '12px',
                            border: '2px solid #e5e5e5'
                          }}>
                            <DatePicker
                              selected={formData.consultationDate}
                              onChange={(date) => setFormData({ ...formData, consultationDate: date })}
                              minDate={new Date()}
                              inline
                              calendarClassName="consultation-calendar"
                              className="consultation-datepicker"
                            />
                          </div>
                          {formData.consultationDate && (
                            <p style={{ 
                              marginTop: '12px', 
                              fontSize: '14px', 
                              color: '#666',
                              textAlign: 'center'
                            }}>
                              Selected: {format(formData.consultationDate, 'EEEE, MMMM d, yyyy')}
                            </p>
                          )}
                        </div>

                        {/* Time Slot Dropdown */}
                        {formData.consultationDate && (
                          <div>
                            <Label htmlFor="consultationTime" style={{ 
                              display: 'block', 
                              fontSize: '16px', 
                              fontWeight: '600', 
                              marginBottom: '12px', 
                              color: '#1a1c1b' 
                            }}>
                              <Clock className="inline mr-2" size={18} />
                              Select Time
                            </Label>
                            <select
                              id="consultationTime"
                              value={formData.consultationTime}
                              onChange={(e) => setFormData({ ...formData, consultationTime: e.target.value })}
                              style={{
                                width: '100%',
                                fontSize: '18px',
                                padding: '18px 24px',
                                borderRadius: '12px',
                                border: '2px solid #e5e5e5',
                                backgroundColor: '#ffffff',
                                color: '#1a1c1b',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#d9fb06';
                                e.target.style.boxShadow = '0 0 0 3px rgba(217, 251, 6, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e5e5';
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              <option value="">Select a time...</option>
                              {timeSlots.map((time) => (
                                <option key={time} value={time}>
                                  {time}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {!formData.consultationDate && (
                          <p style={{ 
                            fontSize: '14px', 
                            color: '#888680',
                            textAlign: 'center',
                            fontStyle: 'italic'
                          }}>
                            Select a date to choose a time slot
                          </p>
                        )}
                      </div>
                    </div>
                  ) : isContactDetailsPage ? (
                    /* Contact Details Page - All fields together */
                    <div>
                      <h2 style={{ 
                        fontSize: '36px', 
                        fontWeight: 'bold', 
                        marginBottom: '48px', 
                        color: '#1a1c1b', 
                        lineHeight: '1.2',
                        letterSpacing: '-0.5px'
                      }}>
                        Contact Details
                      </h2>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {contactFields.map((field) => (
                          <div key={field.id}>
                            <Label 
                              htmlFor={field.field}
                              style={{ 
                                display: 'block', 
                                fontSize: '16px', 
                                fontWeight: '600', 
                                marginBottom: '8px', 
                                color: '#1a1c1b' 
                              }}
                            >
                              {field.question}
                              {field.required && <span style={{ color: '#d9fb06', marginLeft: '4px' }}>*</span>}
                            </Label>
                            <Input
                              id={field.field}
                              name={field.field}
                              type={field.type}
                              value={formData[field.field]}
                              onChange={handleInputChange}
                              placeholder={field.placeholder}
                              required={field.required}
                              disabled={isSubmitting}
                              style={{
                                fontSize: '18px',
                                padding: '18px 24px',
                                borderRadius: '12px',
                                border: '2px solid #e5e5e5',
                                width: '100%',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#d9fb06';
                                e.target.style.boxShadow = '0 0 0 3px rgba(217, 251, 6, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e5e5';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* Regular Question Page */
                    <>
                      <h2 style={{ 
                        fontSize: '36px', 
                        fontWeight: 'bold', 
                        marginBottom: '48px', 
                        color: '#1a1c1b', 
                        lineHeight: '1.2',
                        letterSpacing: '-0.5px'
                      }}>
                        {currentQuestion.question}
                      </h2>

                      {/* Input/Selection Area */}
                      {currentQuestion.type === 'multiple-choice' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {currentQuestion.options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => handleOptionSelect(option)}
                          style={{
                            padding: '20px 24px',
                            background: formData[currentQuestion.field] === option 
                              ? 'linear-gradient(135deg, #d9fb06 0%, #b8d905 100%)' 
                              : '#f9f9f9',
                            border: `2px solid ${formData[currentQuestion.field] === option ? '#d9fb06' : '#e5e5e5'}`,
                            borderRadius: '12px',
                            fontSize: '18px',
                            fontWeight: '500',
                            color: formData[currentQuestion.field] === option ? '#1a1c1b' : '#333',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            textAlign: 'left',
                            width: '100%',
                            boxShadow: formData[currentQuestion.field] === option 
                              ? '0 4px 16px rgba(217, 251, 6, 0.4)' 
                              : '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transform: formData[currentQuestion.field] === option ? 'scale(1.02)' : 'scale(1)'
                          }}
                          onMouseEnter={(e) => {
                            if (formData[currentQuestion.field] !== option) {
                              e.target.style.background = '#f5f5f5';
                              e.target.style.borderColor = '#d9fb06';
                              e.target.style.transform = 'translateX(6px) scale(1.01)';
                              e.target.style.boxShadow = '0 4px 12px rgba(217, 251, 6, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (formData[currentQuestion.field] !== option) {
                              e.target.style.background = '#f9f9f9';
                              e.target.style.borderColor = '#e5e5e5';
                              e.target.style.transform = 'translateX(0) scale(1)';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                            }
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  ) : currentQuestion.type === 'textarea' ? (
                    <div>
                      <Textarea
                        name={currentQuestion.field}
                        value={formData[currentQuestion.field]}
                        onChange={handleInputChange}
                        placeholder={currentQuestion.placeholder}
                        rows={5}
                        disabled={isSubmitting}
                        style={{
                          fontSize: '16px',
                          padding: '18px',
                          borderRadius: '12px',
                          border: '2px solid #e5e5e5',
                          resize: 'vertical',
                          width: '100%',
                          fontFamily: 'inherit',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#d9fb06';
                          e.target.style.boxShadow = '0 0 0 3px rgba(217, 251, 6, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e5e5';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                      ) : (
                        <div>
                          <Input
                            name={currentQuestion.field}
                            type={currentQuestion.type}
                            value={formData[currentQuestion.field]}
                            onChange={handleInputChange}
                            placeholder={currentQuestion.placeholder}
                            required={currentQuestion.required}
                            disabled={isSubmitting}
                            style={{
                              fontSize: '18px',
                              padding: '18px 24px',
                              borderRadius: '12px',
                              border: '2px solid #e5e5e5',
                              width: '100%',
                              transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#d9fb06';
                              e.target.style.boxShadow = '0 0 0 3px rgba(217, 251, 6, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e5e5e5';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="quiz-nav-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '40px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                    style={{ 
                      flex: '1',
                      minWidth: '140px',
                      borderRadius: '8px',
                      color: '#1a1c1b',
                      border: `1px solid ${currentStep === 0 ? '#e5e5e5' : '#1a1c1b'}`,
                      backgroundColor: currentStep === 0 ? '#f5f5f5' : 'transparent',
                      opacity: currentStep === 0 ? 0.6 : 1,
                      cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                      padding: '14px 28px',
                      fontSize: '16px',
                      fontWeight: '600',
                      minHeight: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}
                    onMouseEnter={(e) => {
                      if (currentStep !== 0) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentStep !== 0) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }
                    }}
                  >
                    <ChevronLeft size={18} style={{ color: '#1a1c1b' }} />
                    Previous
                  </button>
                  
                  {!isContactDetailsPage && !isConsultationPage && currentQuestion && !currentQuestion.required && currentStep < totalSteps - 1 && (
                    <button
                      type="button"
                      onClick={handleSkip}
                      style={{ 
                        minWidth: '100px',
                        borderRadius: '8px',
                        color: '#1a1c1b',
                        border: '1px solid #1a1c1b',
                        backgroundColor: 'transparent',
                        padding: '14px 28px',
                        fontSize: '16px',
                        fontWeight: '600',
                        minHeight: '52px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }}
                    >
                      {currentQuestion.skipText}
                    </button>
                  )}
                  
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!isCurrentQuestionValid()}
                    style={{ 
                      flex: '1',
                      minWidth: '140px',
                      borderRadius: '8px',
                      color: !isCurrentQuestionValid() ? '#1a1c1b' : '#1a1c1b',
                      border: `1px solid ${!isCurrentQuestionValid() ? '#e5e5e5' : '#1a1c1b'}`,
                      backgroundColor: !isCurrentQuestionValid() ? '#f5f5f5' : 'transparent',
                      opacity: !isCurrentQuestionValid() ? 0.6 : 1,
                      cursor: !isCurrentQuestionValid() ? 'not-allowed' : 'pointer',
                      padding: '14px 28px',
                      fontSize: '16px',
                      fontWeight: '600',
                      minHeight: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}
                    onMouseEnter={(e) => {
                      if (isCurrentQuestionValid()) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isCurrentQuestionValid()) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#1a1c1b';
                        e.currentTarget.style.borderColor = '#1a1c1b';
                      }
                    }}
                  >
                    {isConsultationPage ? (
                      <>
                        Review <ChevronRight size={18} style={{ color: '#1a1c1b' }} />
                      </>
                    ) : isContactDetailsPage ? (
                      <>
                        Next <ChevronRight size={18} style={{ color: '#1a1c1b' }} />
                      </>
                    ) : (
                      <>
                        Next <ChevronRight size={18} style={{ color: '#1a1c1b' }} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
